import Env from 'src/environmentVariables/EnvironmentVariables';
import { HistoryType } from 'src/@types/historyTables';
import dayjs from 'dayjs';
import { notify } from './notifications';
import { MarketTypeFilter } from 'src/@types/types';
import { MAX_NUM_OF_MONTHS_FETCH_LONG_TERM } from 'src/constants/historyTables';
import { dlog } from '../dev';
import {
	Opaque,
	convertStringValuesToNumbers,
} from '@velocity-exchange/common';
import {
	SpotMarkets,
	PerpMarkets,
	SpotMarketConfig,
	PerpMarketConfig,
	VelocityEnv,
} from '@velocity-exchange/sdk';

const CLUSTER: VelocityEnv = Env.sdkEnv;

/**
 * Get spot market symbol from market index
 */
export const getSpotMarketSymbol = (
	marketIndex: number
): string | undefined => {
	const market = (SpotMarkets[CLUSTER] as SpotMarketConfig[])?.find(
		(m) => m.marketIndex === marketIndex
	);
	return market?.symbol;
};

/**
 * Get perp market symbol from market index
 */
export const getPerpMarketSymbol = (
	marketIndex: number
): string | undefined => {
	const market = (PerpMarkets[CLUSTER] as PerpMarketConfig[])?.find(
		(m) => m.marketIndex === marketIndex
	);
	return market?.symbol;
};

/**
 * This client is used to fetch data from the Data API.
 *
 * You can see the swagger docs for the data API here : https://data.api.velocity.exchange/playground
 *
 * We have split the client into three types of requests:
 * 1. Short term data requests
 * 2. Long term data requests
 * 3. Snapshot data requests
 *
 * Short term requests are used to fetch "recent", in a paginated way back in time.
 * Long term requests are used to fetch data for a specific date range.
 * Snapshot requests are used to fetch account data for a specific number of days ago. They correspond to `user/{accountId}/snapshots` in the swagger docs.
 *
 * For short term requests they will return a pageToken, which is a string, which you can use to fetch the next page of data.
 * - It's important to note that this page token can be considered an index into that point of data that WON'T CHANGE if new data comes in. E.g. if your fetch the most recent 20 records on an endpoint, and store the pageToken for the next 20 records, and then new data comes before you fetch the next 20 records - the result will still be the same, it will be the following 20 records after whatever the last record in your previous fetch was.
 * For long term requests they will return a list of records. Instead of a pageToken they are just paginated by a number.
 */

type DataApiResponse<T> = {
	success: boolean;
	records: T[];
	meta: {
		nextPage: null | string;
		totalRecords?: number;
	};
};

export type ShortTermDataApiPathParams = {
	marketFilter?: MarketTypeFilter;
	orderId?: number;
	userAccount?: string;
	authority?: string;
	marketSymbol?: string;
	optionalMarketSymbol?: string;
};

export type ShortTermDataApiQueryParams = {
	limit?: number;
	sort?: string;
	startTs?: number;
	endTs?: number;
	start?: string; // Date string format (YYYY-MM-DD)
	end?: string; // Date string format (YYYY-MM-DD)
	symbol?: string;
	hasFill?: boolean; // Filter to only return orders with fills
	// pageToken?: string; // Not implemented :: Currently we treat pageToken as a seperate param to pass into the main function. It may be an improvement to generalize it into here
};

type SnapshotDataApiResponse<T> = {
	success: boolean;
	data: T;
};

export type SnapshotApiUserPathParams = {
	userAccount: string;
};

export type SnapshotApiAuthorityPathParams = {
	authority: string;
};

export type SnapshotApiQueryParams = {
	days?: number;
};

const USER_ACCOUNT_PLACEHOLDER = '{userAccount}'; // NOTE:: In the data API swagger docs this is labelled {accountId}
const AUTHORITY_ID_PLACEHOLDER = '{authorityId}';
const MARKET_FILTER_PLACEHOLDER = '{marketFilter}';
const MARKET_SYMBOL_PLACEHOLDER = '{marketSymbol}'; // NOTE:: In the data API swagger docs this is labelled {symbol}
const OPTIONAL_MARKET_SYMBOL_PLACEHOLDER = '{optionalMarketSymbol}'; // NOTE:: In the data API swagger docs this is labelled {symbol}
const ORDER_ID_PLACEHOLDER = '{orderId}';
const YEAR_PLACEHOLDER = '{year}';
const MONTH_PLACEHOLDER = '{month}';
const REPLACEABLE_PLACEHOLDERS_TO_PARAMS = {
	[USER_ACCOUNT_PLACEHOLDER]: 'userAccount',
	[AUTHORITY_ID_PLACEHOLDER]: 'authority',
	[MARKET_FILTER_PLACEHOLDER]: 'marketFilter',
	[MARKET_SYMBOL_PLACEHOLDER]: 'marketSymbol',
	[OPTIONAL_MARKET_SYMBOL_PLACEHOLDER]: 'optionalMarketSymbol',
	[ORDER_ID_PLACEHOLDER]: 'orderId',
	[YEAR_PLACEHOLDER]: 'year',
	[MONTH_PLACEHOLDER]: 'month',
};
const REPLACEABLE_PLACEHOLDERS = Object.keys(
	REPLACEABLE_PLACEHOLDERS_TO_PARAMS
);
// Optional replaceable placeholders are placeholders that are optional and can be omitted from the route. They are always appended with a `/` if they are present.
const OPTIONAL_REPLACEABLE_PLACEHOLDERS = [OPTIONAL_MARKET_SYMBOL_PLACEHOLDER];

export type ShortTermDataApiLookupTypes =
	| HistoryType
	| 'recentMarketTrades'
	| 'positionHistory'
	| 'marketsVolume24h'
	| 'marketsVolume30d'
	| 'leaderboard'
	| 'leaderboardAuthority';

const SHORT_TERM_DATA_API_LOOKUP: Partial<
	Record<ShortTermDataApiLookupTypes, string>
> = {
	deposits: `/user/${USER_ACCOUNT_PLACEHOLDER}/deposits`,
	swaps: `/user/${USER_ACCOUNT_PLACEHOLDER}/swaps`,
	'funding-payments': `/user/${USER_ACCOUNT_PLACEHOLDER}/fundingPayments`,
	'if-staking': `/authority/${AUTHORITY_ID_PLACEHOLDER}/insuranceFundStake`,
	liquidations: `/user/${USER_ACCOUNT_PLACEHOLDER}/liquidations`,
	'settled-balances': `/user/${USER_ACCOUNT_PLACEHOLDER}/settlePnl`,
	trades: `/user/${USER_ACCOUNT_PLACEHOLDER}/trades`,
	recentMarketTrades: `/market/${MARKET_SYMBOL_PLACEHOLDER}/trades`,
	orders: `/user/${USER_ACCOUNT_PLACEHOLDER}/orders/${MARKET_FILTER_PLACEHOLDER}${OPTIONAL_MARKET_SYMBOL_PLACEHOLDER}`,
	orderActions: `/user/${USER_ACCOUNT_PLACEHOLDER}/orders/${ORDER_ID_PLACEHOLDER}/actions`,
	rewards: `/user/${USER_ACCOUNT_PLACEHOLDER}/rewards`,
	positionHistory: `/user/${USER_ACCOUNT_PLACEHOLDER}/positions`,
	marketsVolume24h: `/stats/markets/volume/24h`,
	marketsVolume30d: `/stats/markets/volume/30d`,
	leaderboard: `/stats/leaderboard`,
	leaderboardAuthority: `/stats/leaderboard/${AUTHORITY_ID_PLACEHOLDER}`,
};

const LONG_TERM_DATA_API_LOOKUP: Partial<Record<HistoryType, string>> = {
	deposits: `/user/${USER_ACCOUNT_PLACEHOLDER}/deposits/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	swaps: `/user/${USER_ACCOUNT_PLACEHOLDER}/swaps/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'funding-payments': `/user/${USER_ACCOUNT_PLACEHOLDER}/fundingPayments/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'if-staking': `/authority/${AUTHORITY_ID_PLACEHOLDER}/insuranceFundStake/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	liquidations: `/user/${USER_ACCOUNT_PLACEHOLDER}/liquidations/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	'settled-balances': `/user/${USER_ACCOUNT_PLACEHOLDER}/settlePnl/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	trades: `/user/${USER_ACCOUNT_PLACEHOLDER}/trades/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
	rewards: `/user/${USER_ACCOUNT_PLACEHOLDER}/rewards/${YEAR_PLACEHOLDER}/${MONTH_PLACEHOLDER}`,
};

export type SnapshotApiUserResource = 'trading' | 'earn' | 'vaults';
export type SnapshotApiAuthorityResource = 'referrals';

const SNAPSHOT_DATA_API_USER_LOOKUP: Partial<
	Record<SnapshotApiUserResource, string>
> = {
	trading: `/user/${USER_ACCOUNT_PLACEHOLDER}/snapshots/trading`,
	earn: `/user/${USER_ACCOUNT_PLACEHOLDER}/snapshots/earn`,
	vaults: `/user/${USER_ACCOUNT_PLACEHOLDER}/snapshots/vaults`,
};

const SNAPSHOT_DATA_API_AUTHORITY_LOOKUP: Partial<
	Record<SnapshotApiAuthorityResource, string>
> = {
	referrals: `/authority/${AUTHORITY_ID_PLACEHOLDER}/snapshots/referrals`,
};

const getShortTermDataApiRouteForHistoryPage = (
	historyType: ShortTermDataApiLookupTypes,
	params?: ShortTermDataApiPathParams
) => {
	const route = SHORT_TERM_DATA_API_LOOKUP[historyType];
	if (!route) {
		throw new Error(`History type ${historyType} not found`);
	}

	const placeholdersToReplace = REPLACEABLE_PLACEHOLDERS.filter(
		(placeholder) => {
			return route.includes(placeholder);
		}
	);

	const routeWithPlaceholdersReplaced = placeholdersToReplace.reduce(
		(acc, placeholder) => {
			const arg =
				params?.[
					REPLACEABLE_PLACEHOLDERS_TO_PARAMS[
						placeholder as keyof typeof REPLACEABLE_PLACEHOLDERS_TO_PARAMS
					] as keyof typeof params
				];

			if (OPTIONAL_REPLACEABLE_PLACEHOLDERS.includes(placeholder)) {
				if (arg?.toString()) {
					return acc.replace(placeholder, `/${arg.toString()}`);
				} else {
					return acc.replace(placeholder, '');
				}
			}

			if (arg === undefined || arg === null) {
				throw new Error(`Argument for placeholder '${placeholder}' not found`);
			}

			return acc.replace(placeholder, arg.toString());
		},
		route
	);

	return routeWithPlaceholdersReplaced;
};

const getLongTermDataApiRouteForHistoryPage = (
	historyType: HistoryType,
	params?: {
		userAccount?: string;
		authority?: string;
		month?: number;
		year?: number;
	}
) => {
	const route = LONG_TERM_DATA_API_LOOKUP[historyType];

	if (!route) {
		throw new Error(`History type ${historyType} not found`);
	}

	const placeholdersToReplace = REPLACEABLE_PLACEHOLDERS.filter(
		(placeholder) => {
			return route.includes(placeholder);
		}
	);

	const routeWithPlaceholdersReplaced = placeholdersToReplace.reduce(
		(acc, placeholder) => {
			const arg =
				params?.[
					REPLACEABLE_PLACEHOLDERS_TO_PARAMS[
						placeholder as keyof typeof REPLACEABLE_PLACEHOLDERS_TO_PARAMS
					] as keyof typeof params
				];

			if (arg === undefined || arg === null) {
				throw new Error(`Argument for placeholder '${placeholder}' not found`);
			}

			if (placeholder === MONTH_PLACEHOLDER) {
				return acc.replace(placeholder, `${(params?.month ?? 0) + 1}`);
			}

			return acc.replace(placeholder, arg.toString());
		},
		route
	);

	return routeWithPlaceholdersReplaced;
};

type GetSnapshotDataApiRoutePayload =
	| {
			level: 'user';
			resource: SnapshotApiUserResource;
			params: SnapshotApiUserPathParams & SnapshotApiQueryParams;
	  }
	| {
			level: 'authority';
			resource: SnapshotApiAuthorityResource;
			params: SnapshotApiAuthorityPathParams & SnapshotApiQueryParams;
	  };

const getSnapshotDataApiRoute = (payload: GetSnapshotDataApiRoutePayload) => {
	const route =
		payload.level === 'user'
			? SNAPSHOT_DATA_API_USER_LOOKUP[payload.resource]
			: SNAPSHOT_DATA_API_AUTHORITY_LOOKUP[payload.resource];

	if (!route) {
		throw new Error(
			`Snapshot route for ${payload.level} ${payload.resource} not found`
		);
	}

	if (payload.level === 'user') {
		const { userAccount } = payload.params;

		if (!userAccount) {
			throw new Error(
				`Argument for placeholder '${USER_ACCOUNT_PLACEHOLDER}' not found`
			);
		}

		return route.replace(USER_ACCOUNT_PLACEHOLDER, userAccount);
	}

	const { authority } = payload.params;

	if (!authority) {
		throw new Error(
			`Argument for placeholder '${AUTHORITY_ID_PLACEHOLDER}' not found`
		);
	}

	return route.replace(AUTHORITY_ID_PLACEHOLDER, authority);
};

/**
 * A string representing URL query parameters in the format "param1=value1&param2=value2"
 * This branded type ensures we're working with properly formatted query params
 */
type QueryParamsString = Opaque<string, 'QueryParamsString'>;

const urlSearchParamsToQueryParamsString = (
	searchParams: URLSearchParams
): QueryParamsString => {
	const paramsString = searchParams.toString();
	return (paramsString ? `?${paramsString}` : '') as QueryParamsString; // URLSearchParams.toString() returns a string of the form "param1=value1&param2=value2" so we need to add a ? to the start of it
};

const buildShortTermDataApiQueryParams = (
	pageToken?: string,
	queryParams?: ShortTermDataApiQueryParams
): QueryParamsString => {
	const queryParamsAccumulator = new URLSearchParams();

	if (pageToken) {
		queryParamsAccumulator.set('page', pageToken);
	}

	if (queryParams) {
		// Process each property of ShortTermDataApiQueryParams
		// This approach maintains exhaustive type checking
		const processQueryParam = (key: keyof ShortTermDataApiQueryParams) => {
			switch (key) {
				case 'limit':
					if (queryParams.limit !== undefined) {
						queryParamsAccumulator.set('limit', queryParams.limit.toString());
					}
					break;
				case 'sort':
					if (queryParams.sort !== undefined) {
						queryParamsAccumulator.set('sort', queryParams.sort);
					}
					break;
				case 'startTs':
					if (queryParams.startTs !== undefined) {
						queryParamsAccumulator.set(
							'startTs',
							queryParams.startTs.toString()
						);
					}
					break;
				case 'endTs':
					if (queryParams.endTs !== undefined) {
						queryParamsAccumulator.set('endTs', queryParams.endTs.toString());
					}
					break;
				case 'start':
					if (queryParams.start !== undefined) {
						queryParamsAccumulator.set('start', queryParams.start);
					}
					break;
				case 'end':
					if (queryParams.end !== undefined) {
						queryParamsAccumulator.set('end', queryParams.end);
					}
					break;
				case 'symbol':
					if (queryParams.symbol !== undefined) {
						queryParamsAccumulator.set('symbol', queryParams.symbol);
					}
					break;
				case 'hasFill':
					if (queryParams.hasFill !== undefined) {
						queryParamsAccumulator.set(
							'hasFill',
							queryParams.hasFill.toString()
						);
					}
					break;
				default: {
					// This will cause a TypeScript error if we haven't handled all possible query params
					const _exhaustiveCheck: never = key;
					break; // NOTE :: We are strict typechecking here so that we don't MISS any params - but the params that get included in `queryParams` may include the other path params (because we allow them to be passed together in a single object) -- so we DON'T want to error here.
				}
			}
		};

		// Process each key in the queryParams object
		(
			Object.keys(queryParams) as Array<keyof ShortTermDataApiQueryParams>
		).forEach(processQueryParam);
	}

	return urlSearchParamsToQueryParamsString(queryParamsAccumulator);
};

const buildLongTermDataApiQueryParams = (
	pageToken?: string
): QueryParamsString => {
	const queryParamsAccumulator = new URLSearchParams();

	if (pageToken) {
		queryParamsAccumulator.set('page', pageToken);
	}

	return urlSearchParamsToQueryParamsString(queryParamsAccumulator);
};

export const createDataApiFullUrl = (path: string, queryParams: string) => {
	return `${Env.dataApiUrl}${path}${queryParams}`;
};

/**
 * The classified outcome of a raw GET against the Data API. This is the shared
 * transport core for every Data API client — it never throws and never shapes
 * an envelope; each client decides how the three outcomes map onto its own
 * response contract.
 */
export type DataApiGetResult<TBody> =
	| { ok: true; body: TBody }
	| { ok: false; kind: 'http'; status: number; url: string }
	| { ok: false; kind: 'network'; error: unknown };

/**
 * GET a Data API route and classify the outcome. Never throws:
 * - `{ ok: true, body }` for a 2xx response with a parseable JSON body
 * - `{ ok: false, kind: 'http' }` for a non-2xx response, carrying the status
 *   code and the final (post-redirect) response URL
 * - `{ ok: false, kind: 'network' }` for fetch rejections and JSON parse
 *   failures, carrying the thrown error
 */
export const fetchFromDataApi = async <TBody = unknown>(
	path: string,
	queryParams = ''
): Promise<DataApiGetResult<TBody>> => {
	const fullUrl = createDataApiFullUrl(path, queryParams);

	try {
		const response = await fetch(fullUrl, { headers: new Headers() });

		if (!response.ok) {
			return {
				ok: false,
				kind: 'http',
				status: response.status,
				url: response.url,
			};
		}

		const body = (await response.json()) as TBody;

		return { ok: true, body };
	} catch (error) {
		return { ok: false, kind: 'network', error };
	}
};

const buildSnapshotDataApiQueryParams = (
	queryParams?: SnapshotApiQueryParams
): QueryParamsString => {
	const queryParamsAccumulator = new URLSearchParams();

	if (queryParams?.days) {
		queryParamsAccumulator.set('days', queryParams.days?.toString() ?? '');
	}

	return urlSearchParamsToQueryParamsString(queryParamsAccumulator);
};

class DataApiClient {
	private static readonly MAX_LONG_TERM_PAGINATION_DEPTH = 1; // prevent continuous fetching for accounts with a lot of data

	/**
	 * Envelope normalization on top of the shared transport core
	 * (`fetchFromDataApi`): any failure resolves to an unsuccessful
	 * snapshot envelope for snapshot routes, or an unsuccessful records
	 * envelope otherwise. Never throws.
	 */
	private static async getFromDataApi<T>(
		path: string,
		queryParams: QueryParamsString
	): Promise<DataApiResponse<T> | SnapshotDataApiResponse<T>> {
		const result = await fetchFromDataApi<
			DataApiResponse<T> | SnapshotDataApiResponse<T>
		>(path, queryParams);

		if (result.ok) {
			return result.body;
		}

		if (path.includes('/snapshots/')) {
			return {
				success: false,
				data: {} as T,
			};
		}

		return { success: false, records: [], meta: { nextPage: null } };
	}

	public static async fetchShortTermData<T>(
		historyType: ShortTermDataApiLookupTypes,
		pageToken?: string,
		params?: ShortTermDataApiPathParams & ShortTermDataApiQueryParams
	) {
		const route = getShortTermDataApiRouteForHistoryPage(historyType, params);

		dlog(`history_tables_v2`, `fetching_short_term_data`, {
			route,
			pageToken,
		});

		const queryParamsString = buildShortTermDataApiQueryParams(
			pageToken,
			params
		);

		const result = (await this.getFromDataApi<T>(
			route,
			queryParamsString
		)) as DataApiResponse<T>;

		return result;
	}

	/**
	 * Fetch the necessary data for each month in the given date range. e.g. if the date range is 3rd June to 8th August, we fetch data for June, July and August.
	 * Fetches up to `MAX_NUM_OF_MONTHS_TO_FETCH` months of data, with up to `MAX_LONG_TERM_PAGINATION_DEPTH` pages for each month.
	 * Note that there may be missing data in between, e.g if month 1 has 10 pages, and month 2 has 1 page, only the first `MAX_LONG_TERM_PAGINATION_DEPTH` pages of month 1 will be fetched, before combining the data from month 2
	 * There is no concept of pagination for how we handle long term data, even though there is pagination on each data API. To keep things simple and efficient, since the data fetched is up to 5000 records, we think its sufficient
	 * for most users when we limit by `MAX_LONG_TERM_PAGINATION_DEPTH` of pages per month.
	 */
	public static async fetchLongTermData<T>(
		historyType: HistoryType,
		params?: {
			userAccount?: string;
			authority?: string;
			startDate?: dayjs.Dayjs;
			endDate?: dayjs.Dayjs;
		}
	) {
		const monthsWithinPeriod = this.getMonthsToFetch(
			params?.startDate ?? dayjs(),
			params?.endDate ?? dayjs()
		).sort((a, b) => b.getTime() - a.getTime()); // descending order
		const monthsToFetch = monthsWithinPeriod.slice(
			0,
			MAX_NUM_OF_MONTHS_FETCH_LONG_TERM
		);

		const fetchPage = async (
			route: string,
			records: T[],
			currentPageToken?: string | null,
			depth = 0
		): Promise<DataApiResponse<T>> => {
			if (depth >= this.MAX_LONG_TERM_PAGINATION_DEPTH || !currentPageToken) {
				if (!currentPageToken) {
					notify({
						message:
							'There is too much data to fetch, please narrow down your date range',
						type: 'warning',
					});
				}
				return {
					success: true,
					records,
					meta: { nextPage: null },
				};
			}

			try {
				const queryParamsString =
					buildLongTermDataApiQueryParams(currentPageToken);
				const response = (await this.getFromDataApi<T>(
					route,
					queryParamsString
				)) as DataApiResponse<T>;
				const updatedRecords = [...records, ...response.records];

				if (response.meta.nextPage) {
					return fetchPage(
						route,
						updatedRecords,
						response.meta.nextPage,
						depth + 1
					);
				}

				return {
					success: response.success,
					records: updatedRecords,
					meta: { nextPage: null },
				};
			} catch (err) {
				console.error(err);
				return {
					success: false,
					records: records,
					meta: { nextPage: currentPageToken },
				};
			}
		};

		dlog(`history_tables_v2`, `fetching_long_term_data`, {
			monthsToFetch,
			params,
		});

		const promises = monthsToFetch.map((monthDate) => {
			const month = monthDate.getMonth();
			const year = monthDate.getFullYear();

			const route = getLongTermDataApiRouteForHistoryPage(historyType, {
				userAccount: params?.userAccount,
				authority: params?.authority,
				month,
				year,
			});

			return fetchPage(route, [], '0');
		});

		const monthlyData = await Promise.all(promises);

		const allRecords = monthlyData.flatMap((data) => data.records);

		return {
			success: monthlyData.every((data) => data.success),
			records: allRecords,
		};
	}

	public static async fetchSnapshotData<T>(
		payload: GetSnapshotDataApiRoutePayload
	) {
		const route = getSnapshotDataApiRoute(payload);

		const queryParamsString = buildSnapshotDataApiQueryParams(payload.params);

		const result = (await this.getFromDataApi<T>(
			route,
			queryParamsString
		)) as SnapshotDataApiResponse<T>;

		const { success, ...data } = result;

		// All data values are strings, so we convert them to numbers here for convenience.
		const normalizedData = convertStringValuesToNumbers(data);

		return { success, data: normalizedData as T };
	}

	private static getMonthsToFetch(
		startDate: dayjs.Dayjs,
		endDate: dayjs.Dayjs
	) {
		const months: Date[] = [];
		let currentDate = startDate.clone().startOf('month');
		const endDateMonth = endDate.clone().startOf('month');

		while (currentDate.isSameOrBefore(endDateMonth)) {
			months.push(new Date(currentDate.year(), currentDate.month(), 1));
			currentDate = currentDate.add(1, 'month');
		}

		return months;
	}
}

export default DataApiClient;
